import { Component, type ErrorInfo, type ReactNode } from "react";
import { logger, serializeError } from "@/lib/logger";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Top-level error boundary. Catches render errors anywhere in the app, logs
 * them through the operational logger (persisted as an `error` in `app_log`),
 * and shows a full-page fallback instead of a blank screen.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logger.error("Uncaught render error (AppErrorBoundary)", {
      ...serializeError(error),
      componentStack: info.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-6 text-center">
          <h1 className="text-lg font-semibold text-foreground">
            Something went wrong
          </h1>
          {/* Not muted-foreground: illegible on the dark theme. */}
          <p className="text-sm text-foreground/80">
            An unexpected error occurred. Please refresh the page to try again.
          </p>
          <button
            className="mt-2 rounded-md border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
            onClick={() => window.location.reload()}
          >
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
